import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, TextInput } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager, { MessageSource } from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import TweetActionsManager from '../services/TweetActionsManager';
import AsyncStorage from '../AsyncStorageShim';
import TweetCell from '../components/TweetCell';
import Modal from '../components/Modal';
import ReactionModal from '../components/ReactionModal';
import ListUsersScreen from './ListUsersScreen';
import NotificationSettingsScreen from './NotificationSettingsScreen';
import NewListScreen from './NewListScreen';
import FilterScreen from './FilterScreen';
import { FaEllipsisV, FaFilter, FaUsers, FaBell, FaEdit, FaTrash, FaShare, FaLanguage, FaSync, FaSearch, FaLock, FaBuilding, FaUserFriends } from 'react-icons/fa';
import PercentageLoader from '../components/PercentageLoader';
import { useOrg } from '../hooks/useOrg';


const MainScreen = () => {
  const { t } = useTranslation();
  const { activeOrg, orgs, mode } = useOrg();
  const [tweets, setTweets] = useState([]);
  const [filteredTweets, setFilteredTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [listsNames, setListsNames] = useState([]);
  const [selectedListsNames, setSelectedListsNames] = useState(new Set());
  const [listsData, setListsData] = useState({}); // Store lists data with IDs

  // Modal State
  const [activeModal, setActiveModal] = useState('none');
  const [modalListId, setModalListId] = useState(null);
  const [modalListName, setModalListName] = useState(null);
  const [modalListProps, setModalListProps] = useState({});

  // Reaction Modal State
  const [showReactionModal, setShowReactionModal] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [reactionText, setReactionText] = useState('');
  const [reactionExplanation, setReactionExplanation] = useState('');
  const [reactionError, setReactionError] = useState(null);
  const [currentReactionTweet, setCurrentReactionTweet] = useState(null);
  const [currentReactionListId, setCurrentReactionListId] = useState(null);

  // NEW: per-list pagination / loading state
  const [listStates, setListStates] = useState({}); // { [listName]: { page, loadingMore, hasMore, refreshing } }
  const isFetchingMap = useRef({});

  // Pagination state
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 20; // Smaller page size for smoother scrolling

  // Flag to prevent multiple fetch calls
  const isFetchingRef = useRef(false);

  // Filter state (per-list)
  const [listFilters, setListFilters] = useState({}); // { [listName]: { selectedUsers, fromDate, toDate, dateFilter, selectedSources } }
  // Keep global filter state for the global filter button / navigation-based filter
  const [currentSelectedUsers, setCurrentSelectedUsers] = useState(null);
  const [currentFromDate, setCurrentFromDate] = useState(null);
  const [currentToDate, setCurrentToDate] = useState(null);
  const [currentDateFilter, setCurrentDateFilter] = useState(null);
  const [currentSelectedSources, setCurrentSelectedSources] = useState([
    MessageSource.TWITTER,
    MessageSource.TELEGRAM,
    MessageSource.TIKTOK,
    MessageSource.INSTAGRAM,
    MessageSource.FACEBOOK_GROUP,
    MessageSource.WEBZ,
    MessageSource.TV,
  ]);

  // NEW: Sort state
  const [sortBy, setSortBy] = useState('inserted_at'); // 'created_at' or 'inserted_at'

  // Ref for column content scrollviews
  const columnScrollRefs = useRef({});

  const navigate = useNavigate();
  const location = useLocation();

  // Add state for dropdown menu
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu') && !event.target.closest('.dropdown-trigger')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add custom CSS to document head for better scrolling behavior
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .column-scroll-container::-webkit-scrollbar {
        width: 6px;
      }
      .column-scroll-container::-webkit-scrollbar-track {
        background: var(--border, rgba(0, 0, 0, 0.1));
        border-radius: 3px;
      }
      .column-scroll-container::-webkit-scrollbar-thumb {
        background: var(--text-muted, rgba(255, 255, 255, 0.2));
        border-radius: 3px;
      }
      .column-scroll-container::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary, rgba(255, 255, 255, 0.3));
      }
      .column-scroll-container {
        scrollbar-width: thin;
        scrollbar-color: var(--text-muted) var(--border);
        /* Fix for Chrome/Firefox scrolling */
        min-height: 0;
        max-height: 100%;
      }
      
      .column-card {
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
      }
      
      .column-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
      }
      
      .list-title-container {
        transition: all 0.2s ease;
        cursor: pointer;
      }
      
      .list-title-container:hover {
        transform: scale(1.02);
      }
      
      .dropdown-trigger {
        transition: all 0.2s ease;
      }
      
      .dropdown-trigger:hover {
        background: rgba(77, 166, 255, 0.1) !important;
        color: #4DA6FF !important;
      }
      
      .dropdown-menu {
        position: absolute;
        top: 60px;
        right: 20px;
        background: var(--bg-card);
        backdrop-filter: blur(20px);
        border-radius: 16px;
        border: 1px solid var(--border, rgba(255,255,255,0.1));
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        min-width: 220px;
        z-index: 100;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding: 8px;
        animation: dropdownFadeIn 0.2s ease-out;
      }
      
      @keyframes dropdownFadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      .dropdown-item {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        border-radius: 8px;
        gap: 12px;
        transition: background-color 0.2s;
        color: var(--text-primary);
        font-weight: 500;
        font-size: 14px;
      }
      
      .dropdown-item:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
      
      .dropdown-item svg {
        color: var(--text-secondary);
        font-size: 16px;
      }

      .header-button {
        transition: all 0.2s ease;
      }
      
      .header-button:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .list-tag {
        transition: all 0.2s ease;
      }
      
      .list-tag:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    AnalyticsService.logActivity('view_main_feed');
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleDraftReaction = async (tweet, listId) => {
    setCurrentReactionTweet(tweet);
    setCurrentReactionListId(listId);

    // Check if profile exists first
    const hasProfile = await ListsManager.hasListenerProfile();
    if (!hasProfile) {
      navigate('/writing-style');
      return;
    }

    // Open modal and start loading
    setShowReactionModal(true);
    setReactionLoading(true);
    setReactionError(null);
    setReactionText('');
    setReactionExplanation('');

    try {
      const result = await ListsManager.generateTweetReaction(
        tweet.tweet_content || tweet.translated_content,
        tweet.username || tweet.author_name || 'unknown',
        tweet.list_name,
        null, // list request
        listId // Pass listId
      );

      if (result && result.success) {
        setReactionText(result.reaction_text);
        setReactionExplanation(result.explanation || '');
      } else {
        setReactionError(result?.error_message || 'Failed to generate reaction. Please set up your writing style profile first.');
      }
    } catch (error) {
      console.error('Error generating reaction:', error);
      setReactionError(error.message || 'Failed to generate reaction');
    } finally {
      setReactionLoading(false);
    }
  };

  const handleRegenerateReaction = () => {
    if (currentReactionTweet) {
      handleDraftReaction(currentReactionTweet, currentReactionListId);
    }
  };

  const fetchMessages = useCallback(async (isInitialLoad = true, pageNum = 0) => {
    // Prevent multiple simultaneous fetch calls
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (isInitialLoad) {
      setLoading(true);
      setPage(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = pageNum * ITEMS_PER_PAGE;

      // Fetch tweets, Telegram, TikTok, Instagram, RSS, Webz, and TV messages in parallel
      const [tweetData, telegramData, tiktokData, instagramData, rssData, fbGroupData, webzData, tvData] = await Promise.all([
        ListsManager.fetchAllTweetsForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTelegramMessagesForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTiktoksForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllInstagramsForListener(null, ITEMS_PER_PAGE, offset),
        ListsManager.fetchAllRssItemsForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllFacebookGroupPostsForListener(null, ITEMS_PER_PAGE, offset),
        ListsManager.fetchAllWebzArticlesForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTvSegmentsForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
      ]);

      // Combine and sort the data by the active sort order
      let combinedData = [...tweetData, ...telegramData, ...tiktokData, ...instagramData, ...(rssData || []), ...(fbGroupData || []), ...(webzData || []), ...(tvData || [])];
      combinedData.sort((a, b) => {
        const dateA = new Date(sortBy === 'inserted_at' && a.inserted_at ? a.inserted_at : a.created_at);
        const dateB = new Date(sortBy === 'inserted_at' && b.inserted_at ? b.inserted_at : b.created_at);
        return dateB - dateA;
      });

      if (combinedData && combinedData.length > 0) {
        // If not initial load, append to existing tweets
        if (!isInitialLoad) {
          setTweets(prevTweets => [...prevTweets, ...combinedData]);
        } else {
          setTweets(combinedData);
        }

        // Check if we've reached the end
        if (combinedData.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        } else {
          // Pre-fetch the next page if not at the end
          setTimeout(() => {
            const nextPage = pageNum + 1;
            prefetchNextPage(nextPage);
          }, 1000);
        }

        // Extract list names for filtering (only on initial load)
        if (isInitialLoad) {
          const uniqueLists = extractUniqueLists(combinedData);
          setListsNames(uniqueLists);

          // Fetch all lists to get their IDs
          const lists = await ListsManager.fetchLists(activeOrg?.id);
          if (lists) {
            // Create a map of list names to their data
            const listsMap = {};
            lists.forEach(list => {
              listsMap[list.name] = list;
            });
            setListsData(listsMap);
          }
        }
      } else {
        // No more data
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Handle modal actions
  const handleModalClose = (result) => {
    setActiveModal('none');
    setModalListId(null);
    const listName = modalListName;
    setModalListName(null);
    setModalListProps({});

    if (result === true && listName) {
      // Refresh list content if needed (e.g. after edit)
      handleRefresh(listName);
    } else if (result && result.filterApplied) {
      // Store filters for this specific list
      const {
        selectedUsers,
        fromDate,
        toDate,
        selectedSources
      } = result;

      if (listName) {
        setListFilters(prev => ({
          ...prev,
          [listName]: {
            selectedUsers,
            fromDate: fromDate ? new Date(fromDate) : null,
            toDate: toDate ? new Date(toDate) : null,
            selectedSources: selectedSources || []
          }
        }));
      } else {
        // Global filter (from header filter button)
        setCurrentSelectedUsers(selectedUsers);
        setCurrentFromDate(fromDate ? new Date(fromDate) : null);
        setCurrentToDate(toDate ? new Date(toDate) : null);
        setCurrentSelectedSources(selectedSources || []);
      }
    } else if (result && result.refreshSettings && listName) {
      // Handle notification settings update
      const { notificationMode, notificationFrequency } = result;
      setListsData(prev => ({
        ...prev,
        [listName]: {
          ...prev[listName],
          notification_mode: notificationMode,
          notification_frequency: notificationFrequency
        }
      }));
    }
  };

  // Prefetch next page of data without updating state
  const prefetchNextPage = async (nextPageNum) => {
    try {
      const offset = nextPageNum * ITEMS_PER_PAGE;

      // Silently prefetch data for the next page
      await Promise.all([
        ListsManager.fetchAllTweetsForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTelegramMessagesForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTiktoksForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllInstagramsForListener(null, ITEMS_PER_PAGE, offset),
        ListsManager.fetchAllFacebookGroupPostsForListener(null, ITEMS_PER_PAGE, offset),
        ListsManager.fetchAllWebzArticlesForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchAllTvSegmentsForListener(null, ITEMS_PER_PAGE, offset, searchText, sortBy),
      ]);

      // Data is now in browser cache for faster loading when user scrolls
    } catch (error) {
      console.error('Error prefetching next page:', error);
    }
  };

  // Helper to fetch lists and their first page
  const fetchInitialListsAndMessages = useCallback(async () => {
    setLoading(true);
    try {
      const lists = await ListsManager.fetchLists(activeOrg?.id);
      if (lists && lists.length > 0) {
        const names = lists.map(l => l.name);
        setListsNames(names);

        // Map of list details
        const listsMap = {};
        const initialStates = {};
        lists.forEach(list => {
          listsMap[list.name] = list;
          initialStates[list.name] = { page: 0, loadingMore: false, hasMore: true, refreshing: false, translating: false };
        });
        setListsData(listsMap);
        setListStates(initialStates);

        const BATCH_SIZE = 2;
        for (let i = 0; i < lists.length; i += BATCH_SIZE) {
          const batch = lists.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map(list => fetchMessagesForList(list.name, list.id, true, 0)));
        }
      }
    } catch (err) {
      console.error('Error loading lists:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, mode, sortBy]); // Re-create if org context or sortBy changes

  // Track if we've already retried once
  const hasRetriedInitialFetch = useRef(false);

  // Initial load + re-fetch whenever the user switches org or mode
  useEffect(() => {
    fetchInitialListsAndMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInitialListsAndMessages]);

  // Retry once if no lists were returned on first attempt
  useEffect(() => {
    if (!loading && listsNames.length === 0 && !hasRetriedInitialFetch.current) {
      hasRetriedInitialFetch.current = true;
      fetchInitialListsAndMessages();
    }
  }, [loading, listsNames, fetchInitialListsAndMessages]);

  useEffect(() => {
    // When sortBy changes, we want to re-fetch EVERYTHING from scratch
    if (listsNames.length > 0 && !loading) {
      // Small delay to let React process the state update
      setTimeout(() => {
        fetchInitialListsAndMessages();
      }, 0);
    }
  }, [sortBy]);

  // Filter tweets whenever tweets array or filter criteria change
  useEffect(() => {
    filterTweets();
  }, [tweets, selectedListsNames, searchText, currentSelectedUsers, currentFromDate, currentToDate, currentSelectedSources]);

  useEffect(() => {
    // Check if we're returning from filter screen with filters applied
    if (location.state?.filterApplied) {
      const {
        listId,
        selectedUsers,
        fromDate,
        toDate,
        dateFilter,
        selectedSources
      } = location.state;

      // Update filter state
      setCurrentSelectedUsers(selectedUsers);
      setCurrentFromDate(fromDate ? new Date(fromDate) : null);
      setCurrentToDate(toDate ? new Date(toDate) : null);
      setCurrentDateFilter(dateFilter);
      setCurrentSelectedSources(selectedSources || []);

      // Clear the navigation state to prevent applying filters multiple times
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const extractUniqueLists = (tweetsData) => {
    const lists = new Set(tweetsData.map(tweet => tweet.list_name).filter(Boolean));
    return [...lists];
  };

  const handleListSelect = (listName) => {
    const newSelectedLists = new Set(selectedListsNames);

    if (newSelectedLists.has(listName)) {
      newSelectedLists.delete(listName);
    } else {
      newSelectedLists.add(listName);
      // Track analytics: user selected a list to view
      try {
        const listId = listsData[listName]?.id;
      } catch (e) {
        console.error('Analytics error:', e);
      }
    }

    setSelectedListsNames(newSelectedLists);
  };

  const filterTweets = () => {
    let result = [...tweets];

    // Filter by list name if any are selected
    if (selectedListsNames.size > 0) {
      result = result.filter(tweet => selectedListsNames.has(tweet.list_name));
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(tweet =>
        (tweet.translated_content || tweet.tweet_content || '').toLowerCase().includes(searchLower) ||
        tweet.author_name?.toLowerCase().includes(searchLower) ||
        tweet.username?.toLowerCase().includes(searchLower)
      );
    }

    // Apply advanced filters if they exist
    if (currentSelectedUsers || currentFromDate || currentToDate ||
      (currentSelectedSources && currentSelectedSources.length < 6)) {
      result = applyAdvancedFilters(
        result,
        currentSelectedUsers,
        currentFromDate,
        currentToDate,
        currentSelectedSources
      );
    }

    setFilteredTweets(result);
  };

  const applyAdvancedFilters = (tweetsToFilter, selectedUsers, fromDate, toDate, selectedSources) => {
    return tweetsToFilter.filter(tweet => {
      // Filter by users if specified
      if (selectedUsers && selectedUsers.length > 0) {
        const username = tweet.username || tweet.author_name;
        if (!username || !selectedUsers.includes(username)) {
          return false;
        }
      }

      // Filter by date range if specified
      if (fromDate || toDate) {
        const tweetDate = new Date(tweet.created_at);
        if (fromDate && tweetDate < fromDate) return false;
        if (toDate && tweetDate > toDate) return false;
      }

      // Filter by source if specified
      if (selectedSources && selectedSources.length > 0) {
        if (!selectedSources.includes(tweet.source)) return false;
      }

      return true;
    });
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  const handleFilterButton = () => {
    // Instead of navigating, open the modal
    // Assuming filter button acts as global filter or requires a specific list context.
    // As per previous design, it was navigating. If you want this to open a modal,
    // we need to know for which list. Or if it's global, we need a global filter modal.
    // For now, let's keep it as is or if you want modal based, adapt it.
    // But since the user complained about redirection, let's try to keep it consistent.
    navigate('/filter', {
      state: {
        selectedUsers: currentSelectedUsers,
        fromDate: currentFromDate?.toISOString(),
        toDate: currentToDate?.toISOString(),
        dateFilter: currentDateFilter,
        selectedSources: currentSelectedSources
      }
    });
  };

  // Group tweets by list name, applying per-list filters
  const getTweetsByList = (listName) => {
    let result = filteredTweets.filter(tweet => tweet.list_name === listName);

    // Apply per-list filters if they exist
    const filters = listFilters[listName];
    if (filters) {
      result = applyAdvancedFilters(
        result,
        filters.selectedUsers,
        filters.fromDate,
        filters.toDate,
        filters.selectedSources
      );
    }

    return result;
  };

  // Get active lists to display
  const getActiveListsNames = () => {
    if (selectedListsNames.size > 0) {
      return Array.from(selectedListsNames);
    } else {
      // If no lists selected, return all list names
      return listsNames;
    }
  };

  // Navigate to list details
  const handleListTitlePress = (listName) => {
    const list = listsData[listName];
    if (list && list.id) {
      // Track analytics

      navigate(`/list/${list.id}`, {
        state: {
          listId: list.id,
          listName: listName
        }
      });
    }
  };

  // Handle scroll to load more for a specific list
  const handleLoadMore = (listName) => {
    const state = listStates[listName] || { page: 0, loadingMore: false, hasMore: true };
    if (state.loadingMore || !state.hasMore || isFetchingMap.current[listName]) return;
    const nextPage = (state.page || 0) + 1;
    updateListState(listName, { loadingMore: true });
    const listId = listsData[listName]?.id;
    if (!listId) return;
    fetchMessagesForList(listName, listId, false, nextPage);
  };

  // Handle scroll event for each column
  const handleColumnScroll = (event, listName) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;

    // Start loading more content when user has scrolled 70% of the way down
    // This creates a more seamless endless scrolling experience
    const scrollThreshold = 0.7;
    const scrollPosition = layoutMeasurement.height + contentOffset.y;
    const scrollContentSize = contentSize.height * scrollThreshold;

    if (scrollPosition >= scrollContentSize && !isFetchingMap.current[listName]) {
      handleLoadMore(listName);
    }
  };

  const [isDeleting, setIsDeleting] = useState({});
  const [deleteError, setDeleteError] = useState('');

  // Handle list deletion
  const handleDeleteList = async (listId, listName) => {
    // Update deleting state for this specific list
    setIsDeleting(prev => ({ ...prev, [listId]: true }));
    setDeleteError('');

    try {
      const success = await ListsManager.deleteList(listId);

      if (success) {
        // Remove the list from the UI
        const updatedListsData = { ...listsData };
        delete updatedListsData[listName];
        setListsData(updatedListsData);

        // Remove from selectedListsNames if it was selected
        if (selectedListsNames.has(listName)) {
          const newSelectedLists = new Set(selectedListsNames);
          newSelectedLists.delete(listName);
          setSelectedListsNames(newSelectedLists);
        }

        // Remove list from listsNames
        setListsNames(prevNames => prevNames.filter(name => name !== listName));
      } else {
        setDeleteError('Failed to delete list');
      }
    } catch (err) {
      console.error('Error deleting list:', err);
      setDeleteError('Error deleting list');
    } finally {
      setIsDeleting(prev => ({ ...prev, [listId]: false }));
    }
  };

  // Handle share list
  const handleShareList = (listName, listId) => {
    if (navigator.share) {
      navigator.share({
        title: `ReactFirst List: ${listName}`,
        text: `Check out this list on ReactFirst: ${listName}`,
        url: `${window.location.origin}/list/${listId}`
      }).catch(error => console.log('Error sharing:', error));
    } else {
      alert("Sharing is not available in your browser");
    }
  };

  // NEW: Handle refresh for a specific list
  const handleRefresh = async (listName) => {
    const listId = listsData[listName]?.id;
    if (!listId) return;

    updateListState(listName, { refreshing: true });
    await fetchMessagesForList(listName, listId, true, 0);
    updateListState(listName, { refreshing: false });
  };

  // NEW: Helper to safely update listStates for a specific list
  const updateListState = (listName, updates) => {
    setListStates(prev => ({
      ...prev,
      [listName]: {
        page: 0,
        loadingMore: false,
        hasMore: true,
        refreshing: false,
        translating: false,
        ...(prev[listName] || {}),
        ...updates
      }
    }));
  };

  // NEW: Fetch messages for a single list (initial load or pagination)
  const fetchMessagesForList = async (listName, listId, isInitialLoad = true, pageNum = 0) => {
    // Prevent multiple simultaneous fetch calls per list
    if (isFetchingMap.current[listName]) return;
    isFetchingMap.current[listName] = true;

    if (isInitialLoad) {
      updateListState(listName, { page: 0, loadingMore: false, hasMore: true });
    } else {
      updateListState(listName, { loadingMore: true });
    }

    try {
      const offset = pageNum * ITEMS_PER_PAGE;

      // Fetch all messages without filtering at the API level
      const [tweetData, telegramData, tiktokData, instagramData, rssData, fbGroupData, webzData] = await Promise.all([
        ListsManager.fetchRelevantTweetsFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.getTelegramMessages(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchRelevantTiktoksFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchRelevantInstagramsFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchRelevantRssItemsFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchFacebookGroupPostsFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
        ListsManager.fetchRelevantWebzArticlesFromList(listId, null, ITEMS_PER_PAGE, offset, searchText, sortBy),
      ]);

      // Combine and sort
      let combinedData = [...(tweetData || []), ...(telegramData || []), ...(tiktokData || []), ...(instagramData || []), ...(rssData || []), ...(fbGroupData || []), ...(webzData || [])];
      combinedData.sort((a, b) => {
        const dateA = new Date(sortBy === 'inserted_at' && a.inserted_at ? a.inserted_at : a.created_at);
        const dateB = new Date(sortBy === 'inserted_at' && b.inserted_at ? b.inserted_at : b.created_at);
        return dateB - dateA;
      });

      // Ensure each message has list_name set
      combinedData = combinedData.map(msg => ({ ...msg, list_name: listName }));

      setTweets(prev => {
        if (isInitialLoad) {
          // Get existing translated messages for this list
          const existingTranslated = prev.filter(t =>
            t.list_name === listName &&
            t.isTranslated &&
            t.tweet_content
          );

          // Create a map of translated content
          const translationMap = {};
          existingTranslated.forEach(t => {
            const key = t.tweet_id || t.id;
            if (key) {
              translationMap[key] = {
                content: t.tweet_content,
                isTranslated: true
              };
            }
          });

          // Apply existing translations to new data
          const updatedCombinedData = combinedData.map(msg => {
            const key = msg.tweet_id || msg.id;
            const existingTranslation = key ? translationMap[key] : null;
            if (existingTranslation) {
              return {
                ...msg,
                tweet_content: existingTranslation.content,
                isTranslated: true
              };
            }
            return msg;
          });

          // Remove existing messages for this list then add new ones
          const filteredPrev = prev.filter(t => t.list_name !== listName);
          return [...filteredPrev, ...updatedCombinedData];
        } else {
          return [...prev, ...combinedData];
        }
      });

      // Update list state
      const newHasMore = combinedData.length >= ITEMS_PER_PAGE;
      updateListState(listName, {
        page: pageNum,
        hasMore: newHasMore,
        loadingMore: false
      });
    } catch (error) {
      console.error(`Error fetching messages for list ${listName}:`, error);
      updateListState(listName, { loadingMore: false, hasMore: false });
    } finally {
      isFetchingMap.current[listName] = false;
    }
  };

  // Add userLanguage state and its initialization
  const [userLanguage, setUserLanguage] = useState('en');

  // Add useEffect to fetch user's language preference after the initial useEffect
  useEffect(() => {
    const fetchUserLanguage = async () => {
      const language = await ListsManager.getListenerLanguage();
      setUserLanguage(language);
    };
    fetchUserLanguage();
  }, []);

  // Update isTweetEnglish to check against user's language
  const isTweetEnglish = (tweet) => {
    const lang = tweet?.lang || tweet?.language || tweet?.detected_language || null;
    if (lang && typeof lang === 'string') {
      return lang.toLowerCase().startsWith(userLanguage.toLowerCase());
    }
    return false; // Unknown language; assume not English to attempt translation once
  };

  // NEW: Translate all messages in a specific list/column
  const handleTranslateAllMessages = async (listName) => {
    if (!listName) return;

    // Set translating flag on
    setListStates(prev => ({
      ...prev,
      [listName]: { ...(prev[listName] || {}), translating: true }
    }));

    try {
      const tweetsToTranslate = tweets.filter(t =>
        t.list_name === listName &&
        t.tweet_content &&
        !t.isTranslated &&
        !isTweetEnglish(t)
      );
      if (tweetsToTranslate.length === 0) {
        alert('No messages to translate in this list.');
        return;
      }

      // Translate in parallel (could be heavy – consider backend batch endpoint later)
      const translations = await Promise.all(
        tweetsToTranslate.map(tw => ListsManager.translateTweet(tw.tweet_content, userLanguage))
      );

      // Build a map for quick lookup
      const translatedMap = {};
      tweetsToTranslate.forEach((tw, idx) => {
        const key = tw.tweet_id || tw.id || idx;
        translatedMap[key] = translations[idx] || tw.tweet_content;
      });

      // Update tweets state with translated text
      setTweets(prev => prev.map(t => {
        if (t.list_name === listName) {
          const key = t.tweet_id || t.id || null;
          if (key !== null && translatedMap[key]) {
            return { ...t, tweet_content: translatedMap[key], isTranslated: true };
          }
        }
        return t;
      }));
    } catch (error) {
      console.error('Error translating messages:', error);
      alert('Error translating messages');
    } finally {
      // Clear translating flag
      setListStates(prev => ({
        ...prev,
        [listName]: { ...(prev[listName] || {}), translating: false }
      }));
    }
  };

  // NEW: Translate all messages across all lists
  const handleTranslateAllLists = async () => {
    // Get all active lists
    const activeListNames = getActiveListsNames();
    if (activeListNames.length === 0) {
      alert('No lists available to translate.');
      return;
    }

    // Set translating flag for all lists
    const updatedStates = {};
    activeListNames.forEach(listName => {
      updatedStates[listName] = { ...(listStates[listName] || {}), translating: true };
    });
    setListStates(prev => ({ ...prev, ...updatedStates }));

    try {
      // Get all tweets that need translation
      const tweetsToTranslate = tweets.filter(t =>
        t.tweet_content &&
        !t.isTranslated &&
        !isTweetEnglish(t)
      );
      if (tweetsToTranslate.length === 0) {
        alert('No messages to translate.');
        return;
      }

      // Translate all tweets in parallel
      const translations = await Promise.all(
        tweetsToTranslate.map(tw => ListsManager.translateTweet(tw.tweet_content, userLanguage))
      );

      const translatedMap = {};
      tweetsToTranslate.forEach((tw, idx) => {
        const key = tw.tweet_id || tw.id || idx;
        translatedMap[key] = translations[idx] || tw.tweet_content;
      });

      // Update all tweets with translations
      setTweets(prev => prev.map(t => {
        const key = t.tweet_id || t.id;
        if (key && translatedMap[key]) {
          return { ...t, tweet_content: translatedMap[key], isTranslated: true };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error translating all messages:', error);
      alert('Error translating messages');
    } finally {
      // Clear translating flags
      const clearedStates = {};
      activeListNames.forEach(listName => {
        clearedStates[listName] = { ...(listStates[listName] || {}), translating: false };
      });
      setListStates(prev => ({ ...prev, ...clearedStates }));
    }
  };

  // NEW: column width state for adjustable columns
  const [columnWidth, setColumnWidth] = useState(600); // default width in px
  // Message stats state
  const [messageStats, setMessageStats] = useState(null);
  const [statsWindowMinutes, setStatsWindowMinutes] = useState(60); // 60 for 1h, 1440 for 24h

  // Fetch stats initially and every minute
  useEffect(() => {
    const fetchStats = async () => {
      const stats = await ListsManager.getMessageStats(statsWindowMinutes);
      if (stats) setMessageStats(stats);
    };
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [statsWindowMinutes]);

  // NEW: track expanded/collapsed state of list prompts
  const [expandedPrompts, setExpandedPrompts] = useState({}); // { [listName]: boolean }

  // Toggle expand/collapse for list prompt
  const togglePrompt = (listName) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [listName]: !prev[listName]
    }));
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {/* Column width slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginRight: 32 }}>
        {/* Slider column */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: '500', marginBottom: 2 }}>{t('app.mainScreen.columnWidth')}</Text>
          <input
            type="range"
            min="300"
            max="700"
            value={columnWidth}
            onChange={(e) => setColumnWidth(parseInt(e.target.value, 10))}
            style={{ cursor: 'pointer' }}
          />
        </div>
        {/* Stats indicator */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 12 }}>
          <Text style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>
            {t('app.mainScreen.relevantPosts')}
          </Text>
          <div onClick={() => setStatsWindowMinutes(prev => prev === 60 ? 1440 : 60)} style={{
            backgroundColor: 'rgba(77, 166, 255, 0.15)',
            border: '1px solid rgba(77, 166, 255, 0.3)',
            padding: '4px 10px',
            borderRadius: '12px',
            color: '#4DA6FF',
            fontSize: 12,
            fontWeight: '600',
            cursor: 'pointer',
            userSelect: 'none'
          }}>
            {`${messageStats?.relevant_count ?? 0} ${statsWindowMinutes === 60 ? t('app.mainScreen.per1h') : t('app.mainScreen.per24h')}`}
          </div>
        </div>
      </div>

      {/* Search input */}
      <div style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        marginRight: 32,
        maxWidth: 500
      }}>
        <FaSearch style={{
          position: 'absolute',
          left: 12,
          color: '#6b7280',
          fontSize: 14,
          zIndex: 1
        }} />
        <TextInput
          style={[styles.searchInput, { paddingLeft: 40, width: '100%' }]}
          placeholder={t('app.mainScreen.searchPlaceholder')}
          placeholderTextColor="#6b7280"
          value={searchText}
          onChangeText={handleSearch}
        />
      </div>

      {/* Sort dropdown + Filter button */}
      <View style={styles.headerButtons}>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border, rgba(255,255,255,0.12))',
            borderRadius: 10,
            color: 'var(--text-primary)',
            fontSize: 13,
            fontWeight: '500',
            padding: '7px 10px',
            cursor: 'pointer',
            outline: 'none',
            appearance: 'auto',
          }}
        >
          <option value="created_at" style={{ background: 'var(--bg-deep-void)' }}>{t('app.mainScreen.sortTimeOfPost')}</option>
          <option value="inserted_at" style={{ background: 'var(--bg-deep-void)' }}>{t('app.mainScreen.sortTimeOfAnalysis')}</option>
        </select>
        <TouchableOpacity
          className="header-button"
          style={[styles.headerButton, styles.filterButton]}
          onPress={handleFilterButton}
        >
          <View style={styles.buttonContent}>
            <FaSearch style={styles.buttonIcon} />
            <Text style={styles.buttonText}>{t('app.mainScreen.filter')}</Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <div style={styles.container}>
      {renderHeader()}

      {/* List tags */}
      {listsNames.length > 0 && (
        <div style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--border, rgba(255,255,255,0.08))',
          whiteSpace: 'nowrap',
          overflowX: 'auto',
          display: 'flex',
          gap: '12px'
        }}>
          {listsNames.map((listName, index) => (
            <TouchableOpacity
              key={index}
              className="list-tag"
              style={[
                styles.listTag,
                selectedListsNames.has(listName) && styles.selectedListTag
              ]}
              onPress={() => handleListSelect(listName)}
            >
              <Text
                style={[
                  styles.listTagText,
                  selectedListsNames.has(listName) && styles.selectedListTagText
                ]}
              >
                {listName}
              </Text>
            </TouchableOpacity>
          ))}
        </div>
      )}

      {/* Main content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <PercentageLoader fontSize={64} style={{ marginBottom: 24 }} />
          <Text style={styles.loadingText}>{t('app.mainScreen.loadingMessages')}</Text>
        </View>
      ) : (
        <div style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            padding: '20px 10px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              height: '100%'
            }}>
              {/* Columns */}
              {getActiveListsNames().map((listName) => {
                const listTweets = getTweetsByList(listName);
                const currentList = listsData[listName];

                return (
                  <div key={listName} className="column-card" style={{
                    width: `${columnWidth}px`,
                    minWidth: `${Math.max(columnWidth - 40, 200)}px`,
                    border: '1px solid var(--border, rgba(255,255,255,0.08))',
                    borderRadius: '28px',
                    background: 'var(--bg-card)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: 'var(--shadow-glass)',
                    overflow: 'visible', // Keep visible for dropdown
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    position: 'relative', // Context for stack
                    zIndex: openDropdownId === listName ? 100 : 1, // Elevate when dropdown open
                  }}>
                    {/* Column header */}
                    <div style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid var(--border, rgba(255,255,255,0.08))',
                      background: 'rgba(59, 130, 246, 0.04)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 20
                    }}>
                      {/* Refresh button - Left side */}
                      <div
                        onClick={() => handleRefresh(listName)}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          transition: 'all 0.2s ease',
                          backgroundColor: 'transparent',
                          color: '#94a3b8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = 'rgba(77, 166, 255, 0.1)';
                          e.target.style.color = '#4DA6FF';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#94a3b8';
                        }}
                      >
                        {listStates[listName]?.refreshing ? (
                          <PercentageLoader fontSize={12} />
                        ) : (
                          <FaSync size={16} />
                        )}
                      </div>

                      {/* List title - Center */}
                      <div
                        className="list-title-container"
                        onClick={() => handleListTitlePress(listName)}
                        style={{
                          flex: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: '8px'
                        }}
                      >
                        <div style={{
                          color: 'var(--text-primary)',
                          fontSize: '20px',
                          fontWeight: '700',
                          letterSpacing: '-0.02em'
                        }}>
                          {listName}
                        </div>
                        {currentList?.visibility && (() => {
                          const v = currentList.visibility;
                          const cfg = v === 'org'
                            ? { Icon: FaBuilding, label: t('app.mainScreen.visibilityOrg'), color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.25)' }
                            : v === 'private'
                            ? { Icon: FaLock, label: t('app.mainScreen.visibilityPrivate'), color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' }
                            : { Icon: FaUserFriends, label: t('app.mainScreen.visibilitySpecific'), color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.25)' };
                          return (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: cfg.color,
                              background: cfg.bg,
                              border: `1px solid ${cfg.border}`,
                              borderRadius: '20px',
                              padding: '3px 9px',
                              letterSpacing: '0.02em',
                            }}>
                              <cfg.Icon size={9} />
                              {cfg.label}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Dropdown trigger - Right side */}
                      <div
                        className="dropdown-trigger"
                        onClick={() => setOpenDropdownId(openDropdownId === listName ? null : listName)}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          backgroundColor: openDropdownId === listName ? 'rgba(77, 166, 255, 0.15)' : 'transparent',
                          color: openDropdownId === listName ? '#4DA6FF' : '#94a3b8'
                        }}
                      >
                        <FaEllipsisV size={16} />
                      </div>

                      {/* Dropdown menu */}
                      {openDropdownId === listName && (
                        <div className="dropdown-menu">
                          <div
                            className="dropdown-item"
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                setModalListId(list.id);
                                setModalListName(listName);
                                setActiveModal('users');
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaUsers size={16} />
                            <span>{t('app.mainScreen.viewUsers')}</span>
                          </div>

                          <div
                            className="dropdown-item"
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                setModalListId(list.id);
                                setModalListName(listName);
                                setModalListProps({
                                  notificationMode: list.notification_mode?.toLowerCase() || 'mute',
                                  notificationFrequency: list.notification_frequency
                                });
                                setActiveModal('notifications');
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaBell size={16} />
                            <span>{t('app.mainScreen.notifications')}</span>
                          </div>

                          <div
                            className="dropdown-item"
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                setModalListId(list.id);
                                setModalListName(listName);
                                setModalListProps({
                                  isForEditing: true,
                                  existingListPrompt: list.request || '',
                                  existingListReactionStyle: list.reaction_writing_style || '',
                                  existingIsWideMonitoring: list.is_wide_monitoring || false,
                                  existingVisibility: list.visibility || 'org',
                                });
                                setActiveModal('edit');
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaEdit size={16} />
                            <span>{t('app.mainScreen.editList')}</span>
                          </div>

                          <div
                            className="dropdown-item"
                            onClick={() => {
                              handleTranslateAllMessages(listName);
                              setOpenDropdownId(null);
                            }}
                          >
                            <FaLanguage size={16} />
                            <span>{t('app.mainScreen.translateAll')}</span>
                          </div>

                          <div
                            className="dropdown-item"
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                setModalListId(list.id);
                                setModalListName(listName);
                                setActiveModal('filter');
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaFilter size={16} />
                            <span>{t('app.mainScreen.filterMessages')}</span>
                          </div>

                          <div
                            className="dropdown-item"
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                handleShareList(listName, list.id);
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaShare size={16} />
                            <span>{t('app.mainScreen.shareList')}</span>
                          </div>

                          <div style={{ height: 1, backgroundColor: 'var(--border)', margin: '4px 0' }} />

                          <div
                            className="dropdown-item"
                            style={{ color: '#ff6b6b' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 107, 107, 0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => {
                              let list = listsData[listName];
                              if (!list) list = Object.values(listsData).find(l => l.name === listName);
                              if (list) {
                                handleDeleteList(list.id, listName);
                                setOpenDropdownId(null);
                              }
                            }}
                          >
                            <FaTrash size={16} />
                            <span>{t('app.mainScreen.deleteList')}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* List details section */}
                    {listsData[listName] && listsData[listName].request && (
                      <div style={{
                        padding: '20px',
                        borderBottom: '1px solid var(--border, rgba(255,255,255,0.06))',
                        background: 'rgba(59, 130, 246, 0.02)',
                      }}>
                        {/* Prompt text with collapse/expand */}
                        <div
                          style={{
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            lineHeight: '22px',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            opacity: 0.9,
                            cursor: listsData[listName].request.length > 140 ? 'pointer' : 'default'
                          }}
                          onClick={() => listsData[listName].request.length > 140 && togglePrompt(listName)}
                        >
                          {expandedPrompts[listName] || listsData[listName].request.length <= 140
                            ? `"${listsData[listName].request}"`
                            : `"${listsData[listName].request.substring(0, 140)}..."`}
                        </div>
                      </div>
                    )}

                    {/* Column content */}
                    {listTweets.length > 0 ? (
                      <div style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                      <div
                        className="column-scroll-container"
                        style={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          background: 'transparent',
                          WebkitOverflowScrolling: 'touch'
                        }}
                        onScroll={(event) => {
                          const target = event.target;
                          if (target.scrollHeight - target.scrollTop < target.clientHeight * 1.5) {
                            handleLoadMore(listName);
                          }
                        }}
                      >
                        <div>
                          {listStates[listName]?.translating && (
                            <div style={{
                              padding: '16px',
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, rgba(77, 166, 255, 0.1), rgba(77, 166, 255, 0.05))',
                              borderRadius: '16px',
                              marginBottom: '12px',
                              border: '1px solid rgba(77, 166, 255, 0.2)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
                                <PercentageLoader fontSize={16} />
                                <div style={{ color: '#4DA6FF', fontWeight: '600' }}>{t('app.mainScreen.translatingMessages')}</div>
                              </div>
                            </div>
                          )}

                          {listTweets.map((tweet, index) => (
                            <TweetCell
                              currentUserLanguage={userLanguage}
                              key={`${tweet.tweet_id || tweet.id}-${index}`}
                              tweet={tweet}
                              listId={currentList?.id}
                              onReaction={handleDraftReaction}
                              sortBy={sortBy}
                              highlightText={searchText}
                            />
                          ))}

                          {listStates[listName]?.loadingMore && (
                            <div style={{
                              padding: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'row',
                              gap: '12px'
                            }}>
                              <PercentageLoader fontSize={16} />
                              <div style={{
                                color: '#4DA6FF',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}>
                                {t('app.mainScreen.loadingMoreMessages')}
                              </div>
                            </div>
                          )}

                          {!listStates[listName]?.hasMore && listTweets.length > ITEMS_PER_PAGE && (
                            <div style={{
                              textAlign: 'center',
                              color: '#64748b',
                              padding: '24px',
                              fontSize: '14px',
                              fontWeight: '500',
                              opacity: 0.8
                            }}>
                              🏁 {t('app.mainScreen.reachedEnd')}
                            </div>
                          )}
                        </div>
                      </div>
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '60px 20px',
                        background: 'linear-gradient(180deg, rgba(30, 30, 35, 0.3), rgba(26, 26, 31, 0.1))'
                      }}>
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '16px',
                          opacity: 0.6
                        }}>
                          📭
                        </div>
                        <div style={{
                          color: '#94a3b8',
                          fontSize: '16px',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {t('app.mainScreen.noMessagesYet')}
                        </div>
                        <div style={{
                          color: '#64748b',
                          fontSize: '14px',
                          textAlign: 'center',
                          marginTop: '8px',
                          opacity: 0.8
                        }}>
                          {t('app.mainScreen.messagesWillAppear')}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* No lists message */}
              {listsNames.length === 0 && (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '60px'
                }}>
                  <div style={{
                    fontSize: '64px',
                    marginBottom: '24px',
                    opacity: 0.6
                  }}>
                    📋
                  </div>
                  <div style={{
                    color: '#94a3b8',
                    fontSize: '20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    {t('app.mainScreen.noListsAvailable')}
                  </div>
                  <div style={{
                    color: '#64748b',
                    fontSize: '16px',
                    textAlign: 'center',
                    opacity: 0.8
                  }}>
                    {t('app.mainScreen.createFirstList')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal
        isVisible={activeModal === 'users'}
        onClose={() => handleModalClose()}
        title={t('app.mainScreen.listUsers', { name: modalListName })}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ListUsersScreen
            listId={modalListId}
            initialListName={modalListName}
            onClose={() => handleModalClose()}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'notifications'}
        onClose={() => handleModalClose()}
        title={t('app.mainScreen.notificationSettings')}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <NotificationSettingsScreen
            listId={modalListId}
            listName={modalListName}
            notificationMode={modalListProps.notificationMode}
            notificationFrequency={modalListProps.notificationFrequency}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'edit'}
        onClose={() => handleModalClose()}
        title={t('app.mainScreen.editList')}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <NewListScreen
            isForEditing={true}
            listId={modalListId}
            existingListName={modalListName}
            existingListPrompt={modalListProps.existingListPrompt || ''}
            existingListReactionStyle={modalListProps.existingListReactionStyle || ''}
            existingIsWideMonitoring={modalListProps.existingIsWideMonitoring || false}
            existingVisibility={modalListProps.existingVisibility || 'org'}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'filter'}
        onClose={() => handleModalClose()}
        title={t('app.mainScreen.filterContent')}
      >
        <div style={{ height: '70vh', width: '100%', overflowY: 'hidden' }}>
          <FilterScreen
            listId={modalListId}
            selectedUsers={modalListName && listFilters[modalListName]?.selectedUsers || currentSelectedUsers}
            fromDate={(modalListName && listFilters[modalListName]?.fromDate || currentFromDate)?.toISOString?.() || null}
            toDate={(modalListName && listFilters[modalListName]?.toDate || currentToDate)?.toISOString?.() || null}
            dateFilter={typeof currentDateFilter !== 'undefined' ? currentDateFilter : null}
            selectedSources={modalListName && listFilters[modalListName]?.selectedSources || currentSelectedSources}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <ReactionModal
        isVisible={showReactionModal}
        onClose={() => setShowReactionModal(false)}
        reactionText={reactionText}
        explanation={reactionExplanation}
        isLoading={reactionLoading}
        onRegenerate={handleRegenerateReaction}
        platform={currentReactionTweet?.source === MessageSource.TELEGRAM ? 'telegram' : 'twitter'}
        originalAuthor={currentReactionTweet?.username || currentReactionTweet?.author_name}
        tweetId={currentReactionTweet?.tweet_id || currentReactionTweet?.id}
        error={reactionError}
      />
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'var(--bg-deep-void)',
    backgroundImage: `
      radial-gradient(circle at 15% 50%, rgba(59, 130, 246, 0.08), transparent 25%),
      radial-gradient(circle at 85% 30%, rgba(139, 92, 246, 0.08), transparent 25%)
    `,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(77, 166, 255, 0.05), transparent 50%)',
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  header: {
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderBottomWidth: 1,
    borderBottomColor: 'var(--border, rgba(255,255,255,0.06))',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--bg-card)',
    backdropFilter: 'blur(20px)',
    zIndex: 50,
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 24,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.02em',
    textShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerButton: {
    backgroundColor: 'var(--glass-panel)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    border: '1px solid var(--border, rgba(255,255,255,0.08))',
    transition: 'all 0.2s ease',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buttonIcon: {
    fontSize: 14,
    color: 'var(--text-secondary)',
  },
  buttonText: {
    color: 'var(--text-secondary)',
    fontWeight: '500',
    fontSize: 14,
  },
  searchInput: {
    backgroundColor: 'var(--glass-panel)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: 'var(--text-primary)',
    border: '1px solid var(--border, rgba(255,255,255,0.08))',
    fontSize: 14,
    fontWeight: '500',
    minWidth: 300,
    transition: 'all 0.3s ease',
    outlineStyle: 'none',
  },
  sortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 7,
    paddingHorizontal: 13,
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    marginRight: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  sortPillActive: {
    backgroundColor: 'rgba(77, 166, 255, 0.15)',
    border: '1px solid rgba(77, 166, 255, 0.35)',
  },
  sortPillText: {
    color: '#a0a0ab',
    fontSize: 13,
    fontWeight: '500',
  },
  sortPillTextActive: {
    color: '#ffffff',
  },
  headerIconButton: {
    padding: 0,
  },
  searchContainer: {
    flex: 1,
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 500,
    marginRight: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    paddingLeft: 12,
  },
  searchIcon: {
    color: '#6b7280',
    fontSize: 14,
    flexShrink: 0,
  },
  headerSearchInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingHorizontal: 10,
    paddingVertical: 12,
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '500',
    outlineStyle: 'none',
    border: 'none',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  listTag: {
    backgroundColor: 'var(--glass-panel)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'var(--border)',
    transition: 'all 0.2s',
  },
  selectedListTag: {
    backgroundColor: 'var(--accent-primary)',
    borderColor: 'var(--accent-primary)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  listTagText: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontWeight: '500',
  },
  selectedListTagText: {
    color: 'white',
    fontWeight: '700',
  },
  filterButton: {
    cursor: 'pointer',
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default MainScreen;